import React, { useEffect, useState } from "react";
import { api, brl } from "../../lib/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { ScoreBadge } from "../../components/Score";
import { ClientTierBadge } from "../../components/TierBadge";
import VIPCard from "../../components/VIPCard";
import GoalCofrinho from "../../components/GoalCofrinho";
import RankingPodium from "../../components/RankingPodium";
import {
  Plus, UserPlus, CreditCard, Wallet, TrendingUp, Users, ShoppingBag,
  AlertTriangle, Sparkles, ArrowRight,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] } }),
};

function todayLabel() {
  const s = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function QuickAction({ to, icon: Icon, label, sub, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative flex flex-col gap-3 overflow-hidden rounded-[22px] border border-border bg-gradient-to-b from-white to-[#FFF7FC] p-4 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
    >
      <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand/10 text-brand transition-colors group-hover:bg-brand-grad group-hover:text-white">
        <Icon size={24} />
      </span>
      <div>
        <span className="block text-sm font-semibold text-brand-dark">{label}</span>
        {sub && <span className="mt-0.5 block text-[11px] text-[#9A8A98]">{sub}</span>}
      </div>
    </Link>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tint = "brand", testid, delay = 0 }) {
  const tints = {
    brand: "bg-brand/10 text-brand",
    pink: "bg-brand-pink/10 text-brand-pink",
    success: "bg-emerald-500/10 text-emerald-600",
    warning: "bg-amber-400/15 text-amber-600",
  };
  const bars = {
    brand: "bg-brand", pink: "bg-brand-pink", success: "bg-emerald-500", warning: "bg-amber-400",
  };
  return (
    <motion.div variants={fadeUp} initial="hidden" animate="show" custom={delay}
      className="relative overflow-hidden rounded-[22px] border border-border bg-gradient-to-b from-white to-[#FFFAFD] p-5 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft" data-testid={testid}>
      <span className={`absolute left-0 top-0 h-full w-1 ${bars[tint]}`} />
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium text-[#7A6A78]">{label}</span>
        <span className={`grid h-11 w-11 place-items-center rounded-2xl ${tints[tint]}`}><Icon size={21} /></span>
      </div>
      <div className="mt-3 text-[26px] font-bold leading-none tabular text-brand-dark">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-[#9A8A98]">{sub}</div>}
    </motion.div>
  );
}

export default function ResellerDashboard() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [sales, setSales] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, s, p] = await Promise.all([
        api.get("/clients"),
        api.get("/sales"),
        api.get("/gamification/me/profile").catch(() => ({ data: null })),
      ]);
      setClients(c.data); setSales(s.data); setProfile(p.data);
    })();
  }, []);

  const active = sales.filter((s) => s.status !== "cancelled");
  const totalSold = active.reduce((a, s) => a + Number(s.total || 0), 0);
  const activeSales = sales.filter((s) => s.status === "active").length;
  const defaulters = clients.filter((c) => c.status === "defaulter").length;
  const aReceber = clients.reduce((a, c) => a + Math.max(0, Number(c.open_balance || 0)), 0);
  const avgScore = clients.length ? Math.round(clients.reduce((a, c) => a + Number(c.score || 0), 0) / clients.length) : 0;
  const firstName = (user?.name || "revendedora").split(" ")[0];
  const score = profile?.score ?? 0;
  const scorePct = Math.max(2, Math.min(100, (score / 1000) * 100));

  return (
    <div data-testid="reseller-dashboard" className="space-y-6">
      {/* Welcome card */}
      <motion.div variants={fadeUp} initial="hidden" animate="show"
        className="relative overflow-hidden rounded-3xl bg-brand-grad p-6 text-white shadow-pop sm:p-8">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-pink/30 blur-2xl" />
        <div className="absolute -bottom-12 right-16 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">Olá, {firstName} 👋</h1>
              <p className="mt-1 text-sm text-white/75">{todayLabel()}</p>
            </div>
            {profile?.tier?.label && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold backdrop-blur">
                <Sparkles size={13} /> {profile.tier.label}
              </span>
            )}
          </div>
          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between text-xs text-white/80">
              <span>Seu score de revendedora</span>
              <span className="font-semibold tabular">{score}/1000</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-white transition-all" style={{ width: `${scorePct}%` }} />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick actions */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-brand-dark">Ações rápidas</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <QuickAction to="/revenda/venda/nova" icon={Plus} label="Nova venda" sub="Registrar pedido" testid="qa-new-sale" />
          <QuickAction to="/revenda/clientes/novo" icon={UserPlus} label="Cadastrar cliente" sub="Nova cliente" testid="qa-new-client" />
          <QuickAction to="/revenda/recuperacao" icon={CreditCard} label="Cobranças" sub="Recuperar valores" testid="qa-charges" />
          <QuickAction to="/revenda/clientes" icon={Wallet} label="Minha carteira" sub="Ver clientes" testid="qa-wallet" />
        </div>
      </div>

      {/* Financial summary */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-brand-dark">Resumo financeiro</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <KpiCard icon={TrendingUp} label="Total vendido" value={brl(totalSold)} sub="Acumulado" tint="brand" testid="kpi-total-sold" delay={0} />
          <KpiCard icon={Wallet} label="A receber" value={brl(aReceber)} sub="Em aberto" tint="pink" testid="kpi-receivable" delay={1} />
          <KpiCard icon={Users} label="Clientes" value={clients.length} sub="Na carteira" tint="success" testid="kpi-clients" delay={2} />
          <KpiCard icon={ShoppingBag} label="Vendas ativas" value={activeSales} sub="Em andamento" tint="brand" testid="kpi-active-sales" delay={3} />
          <KpiCard icon={AlertTriangle} label="Inadimplentes" value={defaulters} sub="Requer atenção" tint="warning" testid="kpi-defaulters" delay={4} />
          <KpiCard icon={Sparkles} label="Score médio" value={avgScore} sub="Da carteira" tint="pink" testid="kpi-avg-score" delay={5} />
        </div>
      </div>

      {/* VIP + Goal */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="[&>*]:!rounded-3xl">
          <VIPCard profile={profile} />
        </div>
        <div className="lg:col-span-2"><GoalCofrinho /></div>
      </div>

      <RankingPodium />

      {/* Recent clients + sales */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-border bg-white p-6 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="section-title">Clientes recentes</h2>
            <Link to="/revenda/clientes" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
              Ver todos <ArrowRight size={13} />
            </Link>
          </div>
          <div className="space-y-1">
            {clients.slice(0, 6).map((c) => (
              <Link key={c.id} to={`/revenda/clientes/${c.id}`}
                className="flex items-center justify-between rounded-xl px-2 py-2.5 transition-colors hover:bg-muted">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
                    {(c.name || "?")[0]?.toUpperCase()}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-brand-dark">{c.name}</div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-[#7A6A78]">
                      <ClientTierBadge credit_limit={c.credit_limit} compact />
                      <span>{c.phone}</span>
                    </div>
                  </div>
                </div>
                <ScoreBadge score={c.score} />
              </Link>
            ))}
            {clients.length === 0 && (
              <div className="py-6 text-center text-sm text-[#7A6A78]">
                Nenhum cliente. <Link to="/revenda/clientes/novo" className="font-semibold text-brand underline">Cadastrar agora</Link>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-white p-6 shadow-card">
          <h2 className="section-title mb-4">Vendas recentes</h2>
          <div className="space-y-1">
            {sales.slice(0, 6).map((s) => (
              <Link key={s.id} to={`/vendas/${s.id}`}
                className="flex items-center justify-between rounded-xl px-2 py-2.5 transition-colors hover:bg-muted">
                <div>
                  <div className="text-sm font-medium text-brand-dark">#{s.number} · {s.client_name}</div>
                  <div className="text-xs text-[#7A6A78]">{s.installments}x · {s.payment_method}</div>
                </div>
                <div className="text-sm font-semibold tabular text-brand-dark">{brl(s.total)}</div>
              </Link>
            ))}
            {sales.length === 0 && <div className="py-6 text-center text-sm text-[#7A6A78]">Sem vendas registradas.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
